'use client';

import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import NullState from '@alltrails/denali/components/NullState';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Wildflowers from '@alltrails/denali/icons/Wildflowers';
import useBaseBranchUrl from '@alltrails/install-prompt/hooks/useBaseBranchUrl';
import useIsPage from '@/hooks/useIsPage';
import mobileRoadblock from '../../assets/images/mobileRoadblock.png';
import styles from './CustomRouteMobileRoadblock.module.scss';

const messages = defineMessages({
  CREATE_YOUR_OWN_ROUTE: {
    defaultMessage: 'Create your own route'
  },
  BUILD_CUSTOM_ROUTE_FREE: {
    defaultMessage: 'Build custom routes on desktop for free, or in the app with Peak'
  },
  CONTINUE_IN_APP: {
    defaultMessage: 'Continue in app'
  }
});

const CustomRouteMobileRoadblock = () => {
  const isMobile = useIsMobileSizedScreen();
  const {
    formattedDefaultMessages: { CREATE_YOUR_OWN_ROUTE, BUILD_CUSTOM_ROUTE_FREE, CONTINUE_IN_APP }
  } = useFormatMessage(messages);
  const { isCustomRoute, isMembersCustomRoute, isNewCustomRoute, isSavedCustomRoute } = useIsPage();
  const baseBranchUrl = useBaseBranchUrl();
  const pathname = usePathname();

  // Extract all slugs from pathname in a single pass
  const { routeSlug, memberSlug, trailSlug } = useMemo(() => {
    if (!pathname) {
      return { routeSlug: null, memberSlug: null, trailSlug: null };
    }

    const pathParts = pathname.split('/');

    // extract route slug: /explore/custom-routes/${slug}
    const customRoutesIndex = pathParts.indexOf('custom-routes');
    const routeSlug = customRoutesIndex !== -1 && pathParts[customRoutesIndex + 1] ? pathParts[customRoutesIndex + 1] : null;

    // extract member slug: /members/${slug}/custom-routes
    const membersIndex = pathParts.indexOf('members');
    const memberSlug = membersIndex !== -1 && pathParts[membersIndex + 1] ? pathParts[membersIndex + 1] : null;

    // extract trail slug: /custom-routes/trail/${slug}
    const trailPrefix = '/custom-routes/trail/';
    const trailIndex = pathname.indexOf(trailPrefix);
    const trailSlug = trailIndex !== -1 ? pathname.substring(trailIndex + trailPrefix.length) : null;

    return { routeSlug, memberSlug, trailSlug };
  }, [pathname]);

  const deeplinkPath = useMemo(() => {
    if (!pathname) return 'alltrails://screen/saved/lists/my_maps';

    // /explore/custom-routes/new
    if (isNewCustomRoute && pathname.includes('/new')) {
      return 'alltrails://screen/explore/custom-routes/new';
    }

    // /explore/custom-routes/trail/${slug}
    if (isNewCustomRoute && pathname.includes('/trail/') && trailSlug) {
      return `alltrails://screen/explore/custom-routes/trail/${trailSlug}`;
    }

    // /explore/custom-routes/${slug}
    if (isSavedCustomRoute && routeSlug) {
      return `alltrails://screen/explore/custom-routes/${routeSlug}`;
    }

    // /members/${slug}/custom-routes
    if (isMembersCustomRoute && memberSlug) {
      return `alltrails://members/${memberSlug}/custom-routes`;
    }

    // fallback
    return 'alltrails://screen/saved/lists/my_maps';
  }, [pathname, isNewCustomRoute, isSavedCustomRoute, isMembersCustomRoute, routeSlug, memberSlug, trailSlug]);

  const deeplink = `${baseBranchUrl}&$ios_deeplink_path=${deeplinkPath}&$android_deeplink_path=${deeplinkPath}`;

  if (!isMobile) return null;

  if (isCustomRoute || isMembersCustomRoute) {
    return (
      <div className={styles.container}>
        <div className={styles.imageContainer}>
          <Image className={styles.overlay} src={mobileRoadblock} alt={CREATE_YOUR_OWN_ROUTE} />
          <div className={styles.tooltip}>
            <Wildflowers className={styles.icon} color="--color-text-primary-inverse" />
            <FormattedMessage defaultMessage="Make scenic" />
          </div>
        </div>
        <NullState
          noTopMargin
          title={CREATE_YOUR_OWN_ROUTE}
          description={BUILD_CUSTOM_ROUTE_FREE}
          buttons={{
            primary: { text: CONTINUE_IN_APP, linkInfo: { href: String(deeplink) }, testId: 'continue-in-app-btn' }
          }}
          className={styles.nullState}
        />
      </div>
    );
  }
  return null;
};

export default CustomRouteMobileRoadblock;
